import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useSendBatch,
  usePauseBatch,
  useResumeBatch,
  useCancelBatch,
  useMessages,
  useCancelMessage,
  type Message,
} from '../hooks/useSendBatches';

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    queued: 'bg-yellow-100 text-yellow-800',
    sending: 'bg-blue-100 text-blue-800',
    paused: 'bg-orange-100 text-orange-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-gray-100 text-gray-600',
    sent: 'bg-green-100 text-green-800',
    delivered: 'bg-green-100 text-green-800',
    opened: 'bg-purple-100 text-purple-800',
    replied: 'bg-indigo-100 text-indigo-800',
    bounced: 'bg-red-100 text-red-800',
    failed: 'bg-red-100 text-red-800',
    draft: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

function ProgressBar({ sent, failed, cancelled, total }: {
  sent: number; failed: number; cancelled: number; queued: number; total: number;
}) {
  if (total === 0) return null;
  const sentPct = (sent / total) * 100;
  const failedPct = (failed / total) * 100;
  const cancelledPct = (cancelled / total) * 100;

  return (
    <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden flex">
      <div className="h-3 bg-green-500 transition-all" style={{ width: `${sentPct}%` }} title={`Sent: ${sent}`} />
      <div className="h-3 bg-red-400 transition-all" style={{ width: `${failedPct}%` }} title={`Failed: ${failed}`} />
      <div className="h-3 bg-gray-300 transition-all" style={{ width: `${cancelledPct}%` }} title={`Cancelled: ${cancelled}`} />
    </div>
  );
}

function formatRelativeTime(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(diff);
  if (abs < 60_000) return diff < 0 ? 'just now' : 'in a moment';
  const mins = Math.round(abs / 60_000);
  if (mins < 60) return diff < 0 ? `${mins}m ago` : `in ${mins}m`;
  const hrs = Math.round(mins / 60);
  return diff < 0 ? `${hrs}h ago` : `in ${hrs}h`;
}

function SendTimeline({ messages }: { messages: Message[] }) {
  const queued = messages.filter((m) => m.status === 'queued' && m.scheduledAt);
  if (queued.length === 0) return null;

  return (
    <div className="mt-4">
      <h3 className="text-sm font-medium text-gray-700 mb-2">Scheduled sends</h3>
      <div className="space-y-1 max-h-64 overflow-y-auto">
        {queued.map((msg, i) => (
          <div
            key={msg.id}
            className="flex items-center justify-between text-xs py-1.5 px-3 rounded bg-gray-50 border border-gray-100"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-gray-400 w-6 shrink-0">{i + 1}</span>
              <span className="text-gray-600 truncate">{msg.toEmail}</span>
              <span className="truncate text-gray-500">{msg.subject}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-2">
              {msg.scheduledAt && (
                <span className="text-gray-400">{formatRelativeTime(msg.scheduledAt)}</span>
              )}
              <StatusBadge status={msg.status} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BatchPanel({ batchId }: { batchId: string }) {
  const { data, isLoading, error } = useSendBatch(batchId, true);
  const { data: messagesData } = useMessages({ sendBatchId: batchId, limit: 100 });
  const pause = usePauseBatch();
  const resume = useResumeBatch();
  const cancel = useCancelBatch();
  const cancelMessage = useCancelMessage();

  if (isLoading) {
    return <div className="text-sm text-gray-500 py-8 text-center">Loading batch...</div>;
  }
  if (error || !data) {
    return <div className="text-sm text-red-600 py-4">Failed to load send batch.</div>;
  }

  const { batch, progress, estimatedCompletionAt } = data;
  const isActive = batch.status === 'queued' || batch.status === 'sending';
  const isPaused = batch.status === 'paused';
  const isFinished = batch.status === 'completed' || batch.status === 'cancelled';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-base font-semibold text-gray-900">Send Batch</h2>
            <StatusBadge status={batch.status} />
          </div>
          <p className="text-xs text-gray-400">
            Started {new Date(batch.createdAt).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isActive && (
            <button
              onClick={() => pause.mutate(batchId)}
              disabled={pause.isPending}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Pause
            </button>
          )}
          {isPaused && (
            <button
              onClick={() => resume.mutate(batchId)}
              disabled={resume.isPending}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              Resume
            </button>
          )}
          {!isFinished && (
            <button
              onClick={() => {
                if (confirm('Cancel all remaining sends in this batch?')) {
                  cancel.mutate(batchId);
                }
              }}
              disabled={cancel.isPending}
              className="px-3 py-1.5 text-sm border border-red-300 rounded-md text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              Cancel batch
            </button>
          )}
        </div>
      </div>

      {/* Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span>
            {progress.sent} of {progress.total} sent
            {progress.failed > 0 && ` · ${progress.failed} failed`}
            {progress.cancelled > 0 && ` · ${progress.cancelled} cancelled`}
          </span>
          {estimatedCompletionAt && !isFinished && (
            <span>Est. completion {formatRelativeTime(estimatedCompletionAt)}</span>
          )}
        </div>
        <ProgressBar
          sent={progress.sent}
          failed={progress.failed}
          cancelled={progress.cancelled}
          queued={progress.queued}
          total={progress.total}
        />
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        {[
          { label: 'Queued', value: progress.queued, color: 'text-yellow-600' },
          { label: 'Sent', value: progress.sent, color: 'text-green-600' },
          { label: 'Failed', value: progress.failed, color: 'text-red-600' },
          { label: 'Cancelled', value: progress.cancelled, color: 'text-gray-500' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-gray-50 rounded-md p-3 text-center">
            <div className={`text-xl font-semibold ${color}`}>{value}</div>
            <div className="text-xs text-gray-500">{label}</div>
          </div>
        ))}
      </div>

      {/* Stagger info */}
      <p className="text-xs text-gray-400 mb-4">
        Sending with {batch.staggerMinSeconds}–{batch.staggerMaxSeconds}s randomized delay between emails
      </p>

      {/* Message timeline */}
      {messagesData && (
        <SendTimeline messages={messagesData.messages} />
      )}

      {/* Message list with individual cancel */}
      {messagesData && messagesData.messages.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">All messages</h3>
          <div className="border border-gray-200 rounded-md overflow-hidden divide-y divide-gray-100">
            {messagesData.messages.map((msg) => (
              <div key={msg.id} className="flex items-center justify-between px-3 py-2 text-xs">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-gray-500 truncate">{msg.toEmail}</span>
                  <span className="text-gray-400 truncate hidden sm:block">{msg.subject}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  {msg.sentAt && (
                    <span className="text-gray-400">{new Date(msg.sentAt).toLocaleTimeString()}</span>
                  )}
                  {msg.scheduledAt && !msg.sentAt && (
                    <span className="text-gray-400">{formatRelativeTime(msg.scheduledAt)}</span>
                  )}
                  <StatusBadge status={msg.status} />
                  {msg.status === 'queued' && (
                    <button
                      onClick={() => cancelMessage.mutate(msg.id)}
                      disabled={cancelMessage.isPending}
                      className="text-gray-400 hover:text-red-500 ml-1"
                      title="Cancel this send"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SendQueuePage() {
  const [searchParams] = useSearchParams();
  const batchId = searchParams.get('batch');

  if (batchId) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="mb-4">
          <h1 className="text-xl font-bold text-gray-900">Send Queue</h1>
          <p className="text-sm text-gray-500">Monitor and manage your outreach sends</p>
        </div>
        <BatchPanel batchId={batchId} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Send Queue</h1>
        <p className="text-sm text-gray-500">Monitor and manage your outreach sends</p>
      </div>
      <div className="text-sm text-gray-500 py-12 text-center bg-white border border-gray-200 rounded-lg">
        No batch selected. Send a press list to see the queue here.
      </div>
    </div>
  );
}
